import React, { MouseEvent, useEffect, useRef, useState, FC, ReactNode } from "react";
import { mediaQueries, navbar_width } from "../utils/constants";
import { HamburgerIcon, LogoutIcon, XIcon } from "../components/icons";
import { Button } from "../components/button";
import { useComponentSize } from "../utils/use-size";
import {
  StyledCompanyIcon,
  StyledHomeButton,
  StyledMobileNavbar,
  StyledNavbar,
  StyledNavButtons,
  StyledNavChildren,
} from "../stitches/navbar";
import { LogoutProps, NavButtonsProps } from "../utils/types";
interface DivProps extends StitchesDivProps, NavButtonsProps, LogoutProps {
  children?: ReactNode;
  companyLogo?: string;
  companyLogoAlt: string;
  onHome: () => void;
}

type StitchesDivProps = React.ComponentProps<typeof StyledNavbar>;

export const Navbar: FC<DivProps> = ({
  children,
  companyLogo,
  companyLogoAlt = "company icon",
  navButtons = [],
  onHome = () => console.log("home button"),
  onLogout = () => console.log("logout button"),
  ...props
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const pageRef = useRef<HTMLDivElement>(null);
  const navbarRef = useRef<HTMLDivElement>(null);

  const size = useComponentSize(pageRef);
  const { width } = size;

  useEffect(() => {
    if (width <= mediaQueries.sm) setIsOpen(false);
    else setIsOpen(true);
  }, [width]);

  const handleClickOutside = (e: MouseEvent) => {
    if (width <= mediaQueries.sm) {
      if (navbarRef.current && navbarRef.current.contains(e.target as Node)) {
        // inside click
        return;
      }
      // outside click
      setIsOpen(false);
    } else return;
  };

  return (
    <div
      ref={pageRef}
      onClick={e => {
        handleClickOutside(e);
      }}
      style={{
        display: "flex",
        height: "auto",
        width: "100%",
        position: "relative",
        flexDirection: width <= mediaQueries.sm ? "column" : "row",
      }}
      {...props}
    >
      <StyledNavbar
        ref={navbarRef}
        css={
          isOpen
            ? {
                zIndex: "10 !important",
                "&:before": {
                  content: "",
                  display: "block",
                  left: navbar_width - 1 + "px",
                  top: navbar_width + "px",
                  position: "absolute",
                  height: `calc(100% - ${navbar_width}px)`,
                  width: "1px",
                  boxShadow: "2px 0 10px 2px $colors$neutral_700",
                  opacity: 0.3,
                  zIndex: "10 !important",
                },
                "@sm": {
                  "&:before": { top: 0, height: "100%" },
                },
              }
            : {}
        }
      >
        <StyledMobileNavbar>
          <StyledHomeButton
            onClick={onHome}
            css={{
              "&:before": {
                display: `${!isOpen && "none"}`,
              },
            }}
          >
            <StyledCompanyIcon src={companyLogo} alt={companyLogoAlt} />
          </StyledHomeButton>
          <Button
            variant="outline"
            icon
            squared="xl"
            color="primary"
            css={{
              margin: "auto 30px auto 0",
              "&:not(:hover)": {
                stroke: "$primary_100",
                ringColor: "$primary_100",
              },
              "@sm": {
                display: "none",
              },
            }}
            onClick={() => setIsOpen(prevState => !prevState)}
          >
            {isOpen ? <XIcon /> : <HamburgerIcon />}
          </Button>
        </StyledMobileNavbar>
        {isOpen && <NavButtons navButtons={navButtons} onLogout={onLogout} />}
      </StyledNavbar>
      <StyledNavChildren>{children}</StyledNavChildren>
    </div>
  );
};

const NavButtons: FC<NavButtonsProps & LogoutProps> = ({ navButtons, onLogout }) => {
  return (
    <StyledNavButtons>
      <div
        style={{
          flexGrow: 1,
          height: "100%",
          maxHeight: "calc(100% - 100px)",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: "20px",
          overflowY: "scroll",
        }}
      >
        <div>{navButtons}</div>
      </div>
      <Logout onLogout={onLogout} />
    </StyledNavButtons>
  );
};

const Logout: FC<{ onLogout: () => void }> = ({ onLogout }) => (
  <Button
    css={{
      margin: "10px auto 0 auto",
      flexShrink: 0,
      "&:not(:hover)": {
        stroke: "$primary_100",
      },
    }}
    variant="ghost"
    icon
    squared="2xl"
    color="primary"
    onClick={() => onLogout()}
  >
    <LogoutIcon />
  </Button>
);
